import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  ProgressBar,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faRotateLeft,
  faTrash,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import "./ProjetoDetalhePage.css";
import TaskDetailPanel from "../components/TaskDetailPanel/TaskDetailPanel";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Falha ao carregar dados.");
  }
  return response?.data?.records || response?.data?.data || [];
};

const parseProject = (response) => {
  if (Array.isArray(response?.data)) return response.data[0] || null;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Falha ao carregar o projeto.");
  }
  if (response?.data?.record) return response.data.record;
  if (response?.data?.data && !Array.isArray(response.data.data)) return response.data.data;
  if (response?.data && !response.data.records) return response.data;
  return null;
};

const formatDate = (value) => {
  if (!value) return "Sem prazo";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem prazo";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ProjetoDetalhePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspace } = useWorkspace();

  const [projeto, setProjeto] = useState(null);
  const [tarefas, setTarefas] = useState([]);
  const [members, setMembers] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const tarefasPendentes = useMemo(
    () => tarefas.filter((task) => !task.concluido),
    [tarefas]
  );

  const tarefasConcluidas = useMemo(
    () => tarefas.filter((task) => task.concluido),
    [tarefas]
  );

  const progresso = useMemo(() => {
    if (tarefas.length === 0) return 0;
    return Math.round((tarefasConcluidas.length / tarefas.length) * 100);
  }, [tarefas, tarefasConcluidas]);

  const carregarProjeto = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [proj, tasks] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/tasks`),
      ]);

      const projetoData = parseProject(proj);

      if (!projetoData) {
        throw new Error("Projeto nao encontrado.");
      }

      const workspaceId = projetoData.id_workspace || selectedWorkspace?.id;
      const mem = workspaceId
        ? await api.get(`/workspaces/${workspaceId}/members`)
        : { data: [] };

      const tarefasData = parseCollection(tasks);
      const membrosData = parseCollection(mem);

      setProjeto(projetoData);
      setTarefas(tarefasData);
      setMembers(membrosData);
    } catch (error) {
      console.error("ProjetoDetalhePage: erro ao carregar projeto", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar os detalhes do projeto."
      );
      setProjeto(null);
      setTarefas([]);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarProjeto();
  }, [id]);

  const criarTarefa = async () => {
    if (!newTask.trim() || !projeto) return;

    try {
      await api.post("/tasks", {
        fields: {
          descricao: newTask,
          id_workspace: projeto.id_workspace,
          id_projeto: id,
          id_usuario_responsavel: user?.id,
          concluido: false,
        },
      });

      setNewTask("");
      setToast("Tarefa criada com sucesso.");
      carregarProjeto();
    } catch (error) {
      console.error("ProjetoDetalhePage: erro ao criar tarefa", error);
      setToast(
        error?.response?.data?.message ||
          "Nao foi possivel criar a tarefa."
      );
    }
  };

  const concluir = async (tarefaId) => {
    try {
      await api.put(`/tasks/${tarefaId}`, {
        fields: { concluido: true },
      });
      setToast("Tarefa concluida.");
      carregarProjeto();
    } catch (error) {
      console.error("ProjetoDetalhePage: erro ao concluir tarefa", error);
      setToast("Nao foi possivel concluir a tarefa.");
    }
  };

  const reabrir = async (tarefaId) => {
    try {
      await api.put(`/tasks/${tarefaId}`, {
        fields: { concluido: false },
      });
      setToast("Tarefa reaberta.");
      carregarProjeto();
    } catch (error) {
      console.error("ProjetoDetalhePage: erro ao reabrir tarefa", error);
      setToast("Nao foi possivel reabrir a tarefa.");
    }
  };

  const excluirProjeto = async () => {
    if (!window.confirm("Deseja realmente excluir este projeto?")) return;

    try {
      await api.delete(`/projects/${id}`);
      window.location.href = "/projetos";
    } catch (error) {
      console.error("ProjetoDetalhePage: erro ao excluir projeto", error);
      setToast("Nao foi possivel excluir o projeto.");
    }
  };

  if (loading) {
    return (
      <Container fluid className="project-loading-state">
        <Spinner animation="border" variant="dark" />
      </Container>
    );
  }

  if (errorMessage) {
    return (
      <Container fluid className="project-container">
        <Alert variant="danger" className="mt-4">
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  if (!projeto) {
    return (
      <Container fluid className="project-container">
        <Alert variant="warning" className="mt-4">
          Projeto nao encontrado.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="project-container">
      <div className="project-shell">
        <header className="project-header">
          <div className="project-header-left">
            <Button
              variant="light"
              size="sm"
              className="btn-back"
              onClick={() => window.history.back()}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Voltar
            </Button>

            <div className="project-title-wrapper">
              <div className="project-context-row">
                <Badge bg="dark">
                  {selectedWorkspace?.nome || projeto.workspace_nome || "Workspace"}
                </Badge>
                <Badge bg="light" text="dark">
                  {tarefas.length} tarefa(s)
                </Badge>
              </div>
              <h3 className="project-title">{projeto.descricao}</h3>
              {projeto.detalhe && (
                <small className="project-subtitle">{projeto.detalhe}</small>
              )}
            </div>
          </div>

          <div className="project-header-right">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/perfil")}
            >
              <FontAwesomeIcon icon={faUsers} /> Time do workspace
            </Button>

            <Button variant="outline-danger" size="sm" onClick={excluirProjeto}>
              <FontAwesomeIcon icon={faTrash} /> Excluir
            </Button>
          </div>
        </header>

        <section className="project-summary-grid">
          <Card className="project-summary-card">
            <Card.Body>
              <span>Progresso</span>
              <strong>{progresso}%</strong>
              <ProgressBar now={progresso} className="mt-3" />
            </Card.Body>
          </Card>

          <Card className="project-summary-card">
            <Card.Body>
              <span>Pendentes</span>
              <strong>{tarefasPendentes.length}</strong>
            </Card.Body>
          </Card>

          <Card className="project-summary-card">
            <Card.Body>
              <span>Concluidas</span>
              <strong>{tarefasConcluidas.length}</strong>
            </Card.Body>
          </Card>

          <Card className="project-summary-card">
            <Card.Body>
              <span>Prazo</span>
              <strong>{formatDate(projeto.data_prazo)}</strong>
            </Card.Body>
          </Card>
        </section>

        <section className="project-board">
          <div className="project-column">
            <div className="project-column-header">
              <div>
                <h5>Pendentes</h5>
                <p>Organize execucao, redistribua e destrave.</p>
              </div>
              <Badge bg="warning">{tarefasPendentes.length}</Badge>
            </div>

            <input
              className="task-input"
              placeholder="+ Nova tarefa..."
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && criarTarefa()}
            />

            <div className="project-task-list">
              {tarefasPendentes.map((task) => (
                <div key={task.id} className="task-card">
                  <div className="task-card-main" onClick={() => {
                    setSelectedTask(task);
                    setShowTaskPanel(true);
                  }}>
                    <strong>{task.descricao}</strong>
                    <small>{task.responsavel || "Sem responsavel"}</small>
                    <small>{formatDate(task.vencimento)}</small>
                  </div>

                  <div className="task-actions">
                    <Button size="sm" variant="success" onClick={() => concluir(task.id)}>
                      <FontAwesomeIcon icon={faCheck} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="project-column completed">
            <div className="project-column-header">
              <div>
                <h5>Concluidas</h5>
                <p>Historico de entrega do projeto.</p>
              </div>
              <Badge bg="success">{tarefasConcluidas.length}</Badge>
            </div>

            <div className="project-task-list">
              {tarefasConcluidas.map((task) => (
                <div key={task.id} className="task-card completed-card">
                  <div className="task-card-main" onClick={() => {
                    setSelectedTask(task);
                    setShowTaskPanel(true);
                  }}>
                    <strong>{task.descricao}</strong>
                    <small>{task.responsavel || "Sem responsavel"}</small>
                    <small>{formatDate(task.vencimento)}</small>
                  </div>

                  <div className="task-actions">
                    <Button size="sm" variant="secondary" onClick={() => reabrir(task.id)}>
                      <FontAwesomeIcon icon={faRotateLeft} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <TaskDetailPanel
        show={showTaskPanel}
        onClose={() => {
          setSelectedTask(null);
          setShowTaskPanel(false);
        }}
        task={selectedTask}
        projectId={id}
        reloadTasks={carregarProjeto}
      />

      <ToastContainer position="top-end">
        <Toast show={!!toast} onClose={() => setToast("")} delay={3000} autohide>
          <Toast.Body>{toast}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default ProjetoDetalhePage;
